import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';

/**
 * Numbered pagination footer — "Showing X to Y of N results" plus page buttons
 * and a "Rows per page" dropdown. Purely presentational: the host owns the
 * current page and page size and reloads its data when `pageChange` or
 * `pageSizeChange` fires.
 */
@Component({
  selector: 'app-pagination',
  templateUrl: './pagination.component.html',
  styleUrls: ['./pagination.component.scss'],
  imports: [CommonModule, FormsModule, MatIconModule],
})
export class PaginationComponent {
  /** Current page, 1-based. */
  @Input() page = 1;
  @Input() pageSize = 30;
  @Input() totalItems = 0;
  /** Blocks the controls while the host is loading. */
  @Input() disabled = false;
  /**
   * Prev/next only, with a "Page X of Y" label instead of page buttons. For
   * cursor-paginated endpoints, which can only step one page at a time.
   */
  @Input() sequential = false;
  /** Options offered in the "Rows per page" dropdown. */
  @Input() pageSizeOptions: number[] = [15, 25, 30, 60, 90, 125];
  /** Hides the "Rows per page" dropdown where the host cannot support it. */
  @Input() showPageSize = true;

  @Output() pageChange = new EventEmitter<number>();
  @Output() pageSizeChange = new EventEmitter<number>();

  public get totalPages(): number {
    return Math.max(1, Math.ceil(this.totalItems / this.pageSize));
  }

  /** 1-based index of the first row on the current page ("Showing X to …"). */
  public get rangeStart(): number {
    return this.totalItems ? (this.page - 1) * this.pageSize + 1 : 0;
  }

  public get rangeEnd(): number {
    return Math.min(this.page * this.pageSize, this.totalItems);
  }

  /**
   * Page buttons: a sliding window of 5 pages, with the first/last page and an
   * ellipsis pinned on either side when the window does not reach them.
   */
  public get pageItems(): (number | '…')[] {
    const total = this.totalPages;
    if (total <= 7) {
      return Array.from({ length: total }, (_, i) => i + 1);
    }

    const left = Math.min(Math.max(1, this.page - 2), total - 4);
    const right = left + 4;

    const items: (number | '…')[] = [];
    if (left > 1) {
      items.push(1);
      if (left > 2) {
        items.push('…');
      }
    }
    for (let p = left; p <= right; p++) {
      items.push(p);
    }
    if (right < total) {
      if (right < total - 1) {
        items.push('…');
      }
      items.push(total);
    }
    return items;
  }

  /**
   * The current page size is always shown in the dropdown even if it is not
   * one of the standard options (e.g. a host defaults to a legacy 40).
   */
  public get resolvedPageSizeOptions(): number[] {
    const options = [...this.pageSizeOptions];
    if (this.pageSize && !options.includes(this.pageSize)) {
      options.push(this.pageSize);
    }
    return options.sort((a, b) => a - b);
  }

  public goToPage(page: number | '…'): void {
    if (page === '…' || this.disabled) {
      return;
    }

    const next = Math.min(Math.max(1, page), this.totalPages);
    if (next !== this.page) {
      this.pageChange.emit(next);
    }
  }

  public onPageSizeChange(value: number | string): void {
    const next = Number(value);
    if (!next || next === this.pageSize) {
      return;
    }
    this.pageSizeChange.emit(next);
  }
}
